import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
} from "@mui/material";
import {
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Queue as QueueIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  Event as EventIcon,
  LocationOn as LocationIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import axios from "axios";
import { getLessonTypeStyle } from "../../utils/colorUtils";
import { getAuthHeaders } from "../../utils/auth";
import "../../assets/style/Timetable.css";

const WaitlistManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [waitlists, setWaitlists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: null, // 'promote' or 'remove'
    entry: null,
  });
  const [processing, setProcessing] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);
  const [locations, setLocations] = useState([]);
  const [selectedLocationIndex, setSelectedLocationIndex] = useState(0); // 0 = "All"

  useEffect(() => {
    fetchWaitlists();
  }, [weekOffset]);

  // Fetch all locations
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const headers = await getAuthHeaders();
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_ADDRESS}/lesson-type-locations/`,
          { headers }
        );
        if (response.status === 200) {
          const data = Array.isArray(response.data) ? response.data : response.data.objects || [];
          const sorted = [...data].sort((a, b) => a.name.localeCompare(b.name, "pl"));
          setLocations(sorted);
        }
      } catch (err) {
        console.error("Failed to fetch locations:", err);
      }
    };
    fetchLocations();
  }, []);

  // Derive selected location ID (null = all locations)
  const selectedLocationId = selectedLocationIndex === 0 ? null : locations[selectedLocationIndex - 1]?.id ?? null;

  // Filter waitlists by selected location
  const filteredWaitlists = selectedLocationId
    ? waitlists.filter(entry => entry.location_id === selectedLocationId)
    : waitlists;

  const fetchWaitlists = async () => {
    try {
      setLoading(true);
      setError("");
      
      let headers;
      try {
        headers = await getAuthHeaders();
      } catch (authError) {
        console.error('Authentication error:', authError);
        setError("Błąd uwierzytelniania. Zaloguj się ponownie.");
        setLoading(false);
        return;
      }
      
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/waitlist/?week_offset=${weekOffset}`,
        { headers }
      );

      if (response.status === 200 && response.data.objects) {
        setWaitlists(response.data.objects);
      } else {
        setWaitlists([]);
      }
    } catch (err) {
      console.error("Error fetching waitlists:", err);
      if (err.response?.status === 403 || err.response?.status === 401) {
        setError("Brak uprawnień. Tylko administratorzy mogą przeglądać listy oczekujących.");
      } else {
        setError("Nie udało się pobrać list oczekujących");
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePromote = async (entry) => {
    try {
      setProcessing(true);
      setError("");
      
      const headers = await getAuthHeaders();
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/waitlist/promote`,
        {
          lesson_id: entry.lesson_id,
          user_id: entry.user_id,
        },
        { headers }
      );

      if (response.status === 200) {
        setSuccess(
          `${entry.user_name} został(a) pomyślnie zapisany(a) na zajęcia`
        );
        setConfirmDialog({ open: false, type: null, entry: null });
        await fetchWaitlists();
      }
    } catch (err) {
      console.error("Error promoting user:", err);
      if (err.response?.status === 409) {
        setError("Zajęcia są pełne. Nie można dodać użytkownika.");
      } else if (err.response?.status === 403 || err.response?.status === 401) {
        setError("Brak uprawnień do wykonania tej operacji.");
      } else if (err.response?.data?.detail) {
        setError(`Błąd: ${err.response.data.detail}`);
      } else {
        setError("Nie udało się dodać użytkownika do zajęć");
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleRemove = async (entry) => {
    try {
      setProcessing(true);
      setError("");
      
      const headers = await getAuthHeaders();
      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/waitlist/`,
        {
          data: {
            lesson_id: entry.lesson_id,
            user_id: entry.user_id,
          },
          headers,
        }
      );

      if (response.status === 200) {
        setSuccess(
          `${entry.user_name} został(a) usunięty(a) z listy oczekujących`
        );
        setConfirmDialog({ open: false, type: null, entry: null });
        await fetchWaitlists();
      }
    } catch (err) {
      console.error("Error removing user from waitlist:", err);
      if (err.response?.status === 403 || err.response?.status === 401) {
        setError("Brak uprawnień do wykonania tej operacji.");
      } else {
        setError("Nie udało się usunąć użytkownika z listy oczekujących");
      }
    } finally {
      setProcessing(false);
    }
  };

  const openConfirmDialog = (type, entry) => {
    setConfirmDialog({ open: true, type, entry });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, type: null, entry: null });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("pl-PL", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) {
      return `${diffMins} min temu`;
    } else if (diffHours < 24) {
      return `${diffHours} godz. temu`;
    } else {
      return `${diffDays} dni temu`;
    }
  };

  const addLeadingZeros = (num) => String(num).padStart(2, "0");

  // Get week boundaries
  const getWeekBoundaries = () => {
    const now = new Date();
    const currentDay = now.getDay();
    const diff = currentDay === 0 ? -6 : 1 - currentDay; // Days since Monday

    // Create new Date object without mutating
    const monday = new Date(now);
    monday.setDate(now.getDate() + diff);
    monday.setHours(0, 0, 0, 0);

    // Add week offset
    const weekStart = new Date(monday);
    weekStart.setDate(monday.getDate() + (weekOffset * 7));

    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);

    return { weekStart, weekEnd };
  };

  // Format week range for display
  const getWeekRangeText = () => {
    const { weekStart, weekEnd } = getWeekBoundaries();

    const formatDate = (date) => {
      return date.toLocaleDateString('pl-PL', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    };

    return `${formatDate(weekStart)} - ${formatDate(weekEnd)}`;
  };

  // Helper function to organize waitlist entries into timetable structure
  const organizeWaitlistsForTimetable = () => {
    const timetable = {};
    const weekDayMap = ['Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota', 'Niedziela'];

    filteredWaitlists.forEach(entry => {
      const lessonDate = new Date(entry.lesson_start);
      const dayOfWeek = lessonDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const dayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert to 0 = Monday
      const weekDay = weekDayMap[dayIndex];

      const hours = lessonDate.getHours();
      const minutes = lessonDate.getMinutes();
      const timeSlot = `${addLeadingZeros(hours)}:${addLeadingZeros(minutes)}`;

      if (!timetable[timeSlot]) {
        timetable[timeSlot] = [];
      }

      // Find if this lesson already exists in the time slot
      let lessonEntry = timetable[timeSlot].find(
        l => l.lesson_id === entry.lesson_id && l.week_day === weekDay
      );

      if (!lessonEntry) {
        // Create new lesson entry with waitlist entries array
        lessonEntry = {
          lesson_id: entry.lesson_id,
          lesson_name: entry.lesson_friendly_name || entry.lesson_name,
          lesson_start: entry.lesson_start,
          lesson_trainer: entry.lesson_trainer,
          lesson_bg_color: entry.lesson_bg_color,
          current_people: entry.current_people,
          max_people: entry.max_people,
          week_day: weekDay,
          waitlist_entries: []
        };
        timetable[timeSlot].push(lessonEntry);
      }

      // Add this waitlist entry to the lesson
      lessonEntry.waitlist_entries.push(entry);
    });

    return timetable;
  };

  // Helper function to organize waitlist entries by weekday for mobile view
  const organizeWaitlistsByWeekday = () => {
    const weekDayMap = ['Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota', 'Niedziela'];
    const byWeekday = {};

    filteredWaitlists.forEach(entry => {
      const lessonDate = new Date(entry.lesson_start);
      const dayOfWeek = lessonDate.getDay();
      const dayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const weekDay = weekDayMap[dayIndex];

      if (!byWeekday[weekDay]) {
        byWeekday[weekDay] = [];
      }

      // Find if this lesson already exists
      let lessonEntry = byWeekday[weekDay].find(l => l.lesson_id === entry.lesson_id);

      if (!lessonEntry) {
        lessonEntry = {
          lesson_id: entry.lesson_id,
          lesson_name: entry.lesson_friendly_name || entry.lesson_name,
          lesson_start: entry.lesson_start,
          lesson_trainer: entry.lesson_trainer,
          lesson_bg_color: entry.lesson_bg_color,
          current_people: entry.current_people,
          max_people: entry.max_people,
          waitlist_entries: []
        };
        byWeekday[weekDay].push(lessonEntry);
      }

      lessonEntry.waitlist_entries.push(entry);
    });

    // Sort lessons within each day by start time
    Object.keys(byWeekday).forEach(day => {
      byWeekday[day].sort((a, b) => new Date(a.lesson_start) - new Date(b.lesson_start));
    });

    return byWeekday;
  };

  // Mobile view component
  const MobileWaitlistView = () => {
    const weekDayMap = ['Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota', 'Niedziela'];
    const byWeekday = organizeWaitlistsByWeekday();

    return (
      <Box>
        {weekDayMap.map(day => {
          const lessonsForDay = byWeekday[day] || [];
          if (lessonsForDay.length === 0) return null;

          return (
            <Box key={day} sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  p: 1.5,
                  backgroundColor: 'primary.main',
                  color: 'white',
                  borderRadius: 1,
                  textAlign: 'center',
                  fontWeight: 600
                }}
              >
                {day}
              </Typography>

              {lessonsForDay.map(lesson => {
                const lessonDate = new Date(lesson.lesson_start);
                const startTime = `${addLeadingZeros(lessonDate.getHours())}:${addLeadingZeros(lessonDate.getMinutes())}`;

                return (
                  <Card
                    key={lesson.lesson_id}
                    sx={{
                      mb: 2,
                      boxShadow: 2,
                      '&:hover': {
                        boxShadow: 4,
                      }
                    }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        color: 'white',
                        fontWeight: 600,
                        textAlign: 'center',
                        ...getLessonTypeStyle(lesson.lesson_bg_color),
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {lesson.lesson_name}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {startTime}
                      </Typography>
                    </Box>

                    <CardContent>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          Trener: <strong>{lesson.lesson_trainer}</strong>
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          Zapisanych: <strong>{lesson.current_people}/{lesson.max_people}</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 600 }}>
                          Lista oczekujących: {lesson.waitlist_entries.length}
                        </Typography>
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      {lesson.waitlist_entries.map((entry, idx) => (
                        <Box
                          key={entry.id}
                          sx={{
                            p: 1.5,
                            mb: 1,
                            backgroundColor: idx === 0 ? '#e8f5e9' : '#f5f5f5',
                            borderRadius: 1,
                            border: idx === 0 ? '1px solid #4caf50' : '1px solid #e0e0e0',
                          }}
                        >
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Chip
                                label={`#${entry.position}`}
                                color={entry.position === 1 ? "success" : "default"}
                                size="small"
                              />
                              <Typography variant="body2" fontWeight={500}>
                                {entry.user_name}
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            {formatTimeAgo(entry.created_at)}
                            {entry.pass_type_name && ` • ${entry.pass_type_name}`}
                          </Typography>
                          <Box display="flex" gap={1}>
                            <Button
                              size="small"
                              onClick={() => openConfirmDialog("promote", entry)}
                              startIcon={<PersonAddIcon />}
                              color="primary"
                              variant="contained"
                              fullWidth
                            >
                              Dodaj do zajęć
                            </Button>
                            <Button
                              size="small"
                              onClick={() => openConfirmDialog("remove", entry)}
                              startIcon={<DeleteIcon />}
                              color="error"
                              variant="outlined"
                              fullWidth
                            >
                              Usuń
                            </Button>
                          </Box>
                        </Box>
                      ))}
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          );
        })}
      </Box>
    );
  };

  // Component to render a single hour row in the waitlist timetable
  const SingleWaitlistHour = ({ hour, timeSlot }) => {
    const weekDayMap = ['Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota', 'Niedziela'];
    const timeSlots = [];

    for (let i = 0; i < 7; i++) {
      const dayName = weekDayMap[i];
      const lessonsInDay = hour ? hour.filter(lesson => lesson.week_day === dayName) : [];

      if (lessonsInDay.length > 0) {
        const lessonGroup = lessonsInDay.map((lesson) => {
          const lessonDate = new Date(lesson.lesson_start);
          const startTime = `${addLeadingZeros(lessonDate.getHours())}:${addLeadingZeros(lessonDate.getMinutes())}`;

          return (
            <Box
              key={lesson.lesson_id}
              sx={{
                p: 2,
                mb: 1,
                borderRadius: 2,
                backgroundColor: 'white',
                border: '1px solid #e0e0e0',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease'
              }}
            >
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  textAlign: 'center',
                  mb: 1,
                  ...getLessonTypeStyle(lesson.lesson_bg_color),
                }}
              >
                {lesson.lesson_name}
              </Box>

              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, textAlign: 'center' }}>
                {startTime}
              </Typography>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 0.5 }}>
                Trener: {lesson.lesson_trainer}
              </Typography>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 1 }}>
                Zapisanych: {lesson.current_people}/{lesson.max_people}
              </Typography>

              <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mb: 1, color: 'warning.main', fontWeight: 600 }}>
                Lista oczekujących: {lesson.waitlist_entries.length}
              </Typography>

              <Divider sx={{ my: 1 }} />

              {/* Waitlist entries */}
              {lesson.waitlist_entries.map((entry, idx) => (
                <Box
                  key={entry.id}
                  sx={{
                    p: 1,
                    mb: 0.5,
                    backgroundColor: idx === 0 ? '#e8f5e9' : '#f5f5f5',
                    borderRadius: 1,
                    border: idx === 0 ? '1px solid #4caf50' : '1px solid #e0e0e0',
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Chip
                        label={`#${entry.position}`}
                        color={entry.position === 1 ? "success" : "default"}
                        size="small"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                      <Typography variant="caption" fontWeight={500}>
                        {entry.user_name}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
                    {formatTimeAgo(entry.created_at)}
                    {entry.pass_type_name && ` • ${entry.pass_type_name}`}
                  </Typography>
                  <Box display="flex" gap={0.5} mt={0.5}>
                    <Button
                      size="small"
                      onClick={() => openConfirmDialog("promote", entry)}
                      startIcon={<PersonAddIcon />}
                      color="primary"
                      variant="outlined"
                      sx={{
                        fontSize: '0.65rem',
                        minWidth: 'auto',
                        px: 0.5,
                        py: 0.25,
                        height: 24,
                      }}
                    >
                      Dodaj
                    </Button>
                    <Button
                      size="small"
                      onClick={() => openConfirmDialog("remove", entry)}
                      startIcon={<DeleteIcon />}
                      color="error"
                      variant="outlined"
                      sx={{
                        fontSize: '0.65rem',
                        minWidth: 'auto',
                        px: 0.5,
                        py: 0.25,
                        height: 24,
                      }}
                    >
                      Usuń
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          );
        });

        timeSlots.push(
          <td key={`lesson-group-${i}`} style={{ padding: '16px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
            {lessonGroup}
          </td>
        );
      } else {
        timeSlots.push(
          <td
            key={`empty-${i}`}
            style={{
              backgroundColor: '#f8f9fa',
              height: '120px',
              border: '1px solid #e0e0e0'
            }}
          />
        );
      }
    }

    return <tr>{timeSlots}</tr>;
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="300px"
      >
        <Box textAlign="center">
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6">Ładowanie list oczekujących...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h5" component="h2" gutterBottom>
            Zarządzanie listami oczekujących
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Zarządzaj użytkownikami na listach oczekujących na zajęcia. Lista wyświetla zajęcia z oczekującymi w formie tygodniowego grafiku.
          </Typography>
          <Typography variant="body1" sx={{ mt: 1, fontWeight: 500, color: 'primary.main' }}>
            {getWeekRangeText()}
          </Typography>
        </Box>
        <Box display="flex" gap={2} flexWrap="wrap">
          <Button
            onClick={() => setWeekOffset(weekOffset - 1)}
            variant="outlined"
            size={isMobile ? "small" : "medium"}
          >
            {isMobile ? 'Poprzedni' : 'Poprzedni tydzień'}
          </Button>
          <Button
            onClick={() => setWeekOffset(0)}
            variant="outlined"
            disabled={weekOffset === 0}
            size={isMobile ? "small" : "medium"}
          >
            {isMobile ? 'Obecny' : 'Obecny tydzień'}
          </Button>
          <Button
            onClick={() => setWeekOffset(weekOffset + 1)}
            variant="outlined"
            size={isMobile ? "small" : "medium"}
          >
            {isMobile ? 'Następny' : 'Następny tydzień'}
          </Button>
          <IconButton onClick={fetchWaitlists} color="primary" size={isMobile ? "medium" : "large"}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Success/Error Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess("")}
        >
          {success}
        </Alert>
      )}

      {/* Location Tabs */}
      {locations.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Tabs
            value={selectedLocationIndex}
            onChange={(_, newIndex) => setSelectedLocationIndex(newIndex)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                minHeight: 42,
              },
            }}
          >
            <Tab
              label="Wszystkie lokalizacje"
              icon={<LocationIcon fontSize="small" />}
              iconPosition="start"
            />
            {locations.map((location) => (
              <Tab
                key={location.id}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {location.name}
                    {!location.show_in_timetable && (
                      <VisibilityOffIcon fontSize="small" sx={{ color: 'text.disabled', ml: 0.5 }} titleAccess="Ukryta w grafiku publicznym" />
                    )}
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>
      )}

      {/* Summary Card */}
      <Card sx={{ mb: 3, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2}>
            <QueueIcon sx={{ fontSize: 48, color: "white" }} />
            <Box>
              <Typography variant="h3" sx={{ color: "white", fontWeight: "bold" }}>
                {filteredWaitlists.length}
              </Typography>
              <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.9)" }}>
                Osób na listach oczekujących
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Timetable View - Desktop or Mobile */}
      {Object.keys(organizeWaitlistsForTimetable()).length > 0 ? (
        isMobile ? (
          <MobileWaitlistView />
        ) : (
        <Box sx={{
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          overflow: 'auto',
          backgroundColor: 'white'
        }}>
          <div id="timetable">
            <div className="table-responsive" style={{ overflowX: 'auto' }}>
              <table
                className="table table-bordered text-center white-table"
                style={{
                  marginBottom: 0,
                  minWidth: '800px'
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th className="text-uppercase" style={{
                      padding: '16px',
                      fontWeight: 600,
                      color: '#333',
                      fontSize: '0.875rem',
                      minWidth: '100px'
                    }}>
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Poniedziałek
                      </Box>
                      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        PON
                      </Box>
                    </th>
                    <th className="text-uppercase" style={{
                      padding: '16px',
                      fontWeight: 600,
                      color: '#333',
                      fontSize: '0.875rem',
                      minWidth: '100px'
                    }}>
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Wtorek
                      </Box>
                      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        WT
                      </Box>
                    </th>
                    <th className="text-uppercase" style={{
                      padding: '16px',
                      fontWeight: 600,
                      color: '#333',
                      fontSize: '0.875rem',
                      minWidth: '100px'
                    }}>
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Środa
                      </Box>
                      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        ŚR
                      </Box>
                    </th>
                    <th className="text-uppercase" style={{
                      padding: '16px',
                      fontWeight: 600,
                      color: '#333',
                      fontSize: '0.875rem',
                      minWidth: '100px'
                    }}>
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Czwartek
                      </Box>
                      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        CZ
                      </Box>
                    </th>
                    <th className="text-uppercase" style={{
                      padding: '16px',
                      fontWeight: 600,
                      color: '#333',
                      fontSize: '0.875rem',
                      minWidth: '100px'
                    }}>
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Piątek
                      </Box>
                      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        PT
                      </Box>
                    </th>
                    <th className="text-uppercase" style={{
                      padding: '16px',
                      fontWeight: 600,
                      color: '#333',
                      fontSize: '0.875rem',
                      minWidth: '100px'
                    }}>
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Sobota
                      </Box>
                      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        SO
                      </Box>
                    </th>
                    <th className="text-uppercase" style={{
                      padding: '16px',
                      fontWeight: 600,
                      color: '#333',
                      fontSize: '0.875rem',
                      minWidth: '100px'
                    }}>
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Niedziela
                      </Box>
                      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        ND
                      </Box>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(organizeWaitlistsForTimetable())
                    .sort((a, b) => {
                      const [hourA, minuteA] = a.split(':').map(Number);
                      const [hourB, minuteB] = b.split(':').map(Number);

                      if (hourA !== hourB) {
                        return hourA - hourB;
                      }
                      return minuteA - minuteB;
                    })
                    .map((timeSlot) => {
                      const lessonsAtTime = organizeWaitlistsForTimetable()[timeSlot];
                      return (
                        <SingleWaitlistHour
                          key={timeSlot}
                          hour={lessonsAtTime}
                          timeSlot={timeSlot}
                        />
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </Box>
        )
      ) : (
        <Box sx={{
          p: 6,
          textAlign: 'center',
          border: '2px dashed #e0e0e0',
          borderRadius: 2,
          backgroundColor: '#fafafa'
        }}>
          <QueueIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Brak osób na listach oczekujących
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            W wybranym tygodniu nie ma użytkowników na listach oczekujących.
          </Typography>
        </Box>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {confirmDialog.type === "promote"
            ? "Potwierdź dodanie do zajęć"
            : "Potwierdź usunięcie z listy"}
        </DialogTitle>
        <DialogContent>
          {confirmDialog.entry && (
            <Box>
              <Typography variant="body1" gutterBottom>
                {confirmDialog.type === "promote"
                  ? `Czy na pewno chcesz dodać użytkownika ${confirmDialog.entry.user_name} do zajęć?`
                  : `Czy na pewno chcesz usunąć użytkownika ${confirmDialog.entry.user_name} z listy oczekujących?`}
              </Typography>
              <Box mt={2} p={2} sx={{ backgroundColor: "grey.100", borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Zajęcia:</strong> {confirmDialog.entry.lesson_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Data:</strong>{" "}
                  {formatDateTime(confirmDialog.entry.lesson_start)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Pozycja na liście:</strong> #{confirmDialog.entry.position}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog} disabled={processing}>
            Anuluj
          </Button>
          <Button
            onClick={() =>
              confirmDialog.type === "promote"
                ? handlePromote(confirmDialog.entry)
                : handleRemove(confirmDialog.entry)
            }
            color={confirmDialog.type === "promote" ? "primary" : "error"}
            variant="contained"
            disabled={processing}
            startIcon={
              processing ? (
                <CircularProgress size={20} />
              ) : confirmDialog.type === "promote" ? (
                <PersonAddIcon />
              ) : (
                <DeleteIcon />
              )
            }
          >
            {processing
              ? "Przetwarzanie..."
              : confirmDialog.type === "promote"
              ? "Dodaj do zajęć"
              : "Usuń z listy"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WaitlistManagement;
